import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './UploadMembersPage.module.css';
// Card 54613. Every call below goes through this client so it inherits the session's
// `Authorization: Bearer` header, token refresh and 401 handling. The page no longer reads a
// credential out of the URL: `/upload-members` sits behind ProtectedRoute + PermissionRoute,
// so the SESSION is the credential and there is nothing left to put in a query string.
import api from '../../services/api';

const INITIAL_FORM = {
  email: '', first_name: '', last_name: '', phone: '',
  address: '', city: '', state: '', zip: '',
  birth_date: '', gender: '',
};

/**
 * The session check has THREE outcomes, not two. A 401/403 is the server saying this session may not
 * use the console; a 5xx or a request that never completed is the server saying nothing at all.
 * Collapsing both onto one `authorized` boolean makes an unreachable API render as an accusation
 * against a valid session, and sends the user to an administrator who has nothing to fix.
 */
const CHECK = {
  pending: 'pending',
  authorized: 'authorized',
  denied: 'denied',
  unavailable: 'unavailable',
};

/** Pure: an HTTP response -> the state it justifies. Anything that is not a verdict is `unavailable`. */
const deriveCheckState = ({ ok, status }) =>
  ok ? CHECK.authorized : (status === 401 || status === 403 ? CHECK.denied : CHECK.unavailable);

/**
 * Pure: is this filename a CSV? Deliberately case-INSENSITIVE. The input's own `accept=".csv"`
 * matches extensions case-insensitively, so the file dialog offers `MEMBERS.CSV` — the ordinary
 * output of an Excel-on-Windows export — as a valid choice. A case-sensitive test here rejected
 * exactly the files the dialog had just told the user were acceptable.
 */
const isCsvFile = (name) => /\.csv$/i.test(name);

/**
 * Pure: a resend-activation response -> the inline alert it justifies. The SERVER owns the copy on
 * every branch — its 409 message carries the member's effective date, which this page cannot derive
 * — so each arm prefers the body and only falls back to the sentence this page must show when the
 * body is empty or unparseable. A 404 is spelled out here because "no member found" is the partner's
 * most common outcome and must read the same even if the server's body changes shape.
 */
const deriveResendResult = (res, data) => {
  if (res.ok) return { type: 'success', message: data.message || 'Activation email sent.' };
  if (res.status === 404) return { type: 'error', message: data.error || 'No member found with that email' };
  if (res.status === 429) return { type: 'error', message: data.error || data.message || 'Please wait a moment before resending to this address again' };
  return { type: 'error', message: data.message || data.error || 'Could not resend the activation email. Please try again.' };
};

const UploadMembersPage = () => {
  const [checkState, setCheckState] = useState(CHECK.pending);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [singleResult, setSingleResult] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const resendInFlight = useRef(false);
  const fileRef = useRef(null);

  const runTokenCheck = useCallback(async () => {
    setCheckState(CHECK.pending);
    try {
      await api.get('/member-upload/validate');
      setCheckState(deriveCheckState({ ok: true }));
    } catch (err) {
      // `api` throws on any non-2xx and carries the HTTP status on the error, so a 401/403 stays
      // distinguishable from a transport failure that never reached the server.
      setCheckState(deriveCheckState({ ok: false, status: err?.status }));
    }
  }, []);

  useEffect(() => { runTokenCheck(); }, [runTokenCheck]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setSingleResult(null);
    setSubmitting(true);
    try {
      const data = await api.post('/member-upload/single', form);
      setSingleResult({ type: 'success', message: `Member ${data.action || 'created'}: ${data.member?.email || data.user?.email || form.email}` });
      setForm(INITIAL_FORM);
    } catch (err) {
      // Keep the two failure shapes distinct, exactly as the old two-branch code did: a transport
      // failure has no status and reports its own message; an HTTP refusal reports the server's.
      const message = err.status === undefined
        ? (err.message || 'Network error')
        : (err.data?.error || err.data?.message || 'Failed to create member');
      setSingleResult({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isCsvFile(file.name)) {
      setCsvFile(file);
      setCsvResult(null);
      return;
    }
    // A rejected pick must clear BOTH the state and the input. Leaving the previous selection in
    // place put a stale filename on screen next to an enabled button, so Upload would have sent
    // the earlier file rather than the one the user had just chosen.
    setCsvFile(null);
    if (fileRef.current) fileRef.current.value = '';
    setCsvResult({ type: 'error', message: `${file.name} is not a .csv file. Choose a file ending in .csv.` });
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    setUploading(true);
    setCsvResult(null);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      // `api.upload` sends the FormData untouched (no JSON Content-Type is set) and attaches the
      // session header itself, so the csv card inherits the same auth as the other two.
      const data = await api.upload('/member-upload/csv', formData);
      setCsvResult({ type: 'success', data });
      setCsvFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      const message = err.status === undefined
        ? (err.message || 'Network error')
        : (err.data?.error || err.data?.message || 'Upload failed');
      setCsvResult({ type: 'error', message });
    } finally {
      setUploading(false);
    }
  };

  /**
   * Resend is one address per click and keeps its own state, so a slow resend never disables or
   * clears the single-member form or the CSV uploader beside it.
   *
   * The in-flight guard is a REF, not the `resending` state. Two clicks dispatched in the same tick
   * (Enter inside the field plus a click, or a synthetic double click) both run against the closure
   * of the render that is still on screen, where `resending` is still false — measured on this page:
   * three synchronous clicks sent THREE requests and produced three activation emails for one press.
   * The ref flips synchronously, so the second invocation returns before it can send anything. The
   * `disabled` attribute still drives the visible "Sending..." state.
   */
  const handleResendSubmit = async (e) => {
    e.preventDefault();
    const email = resendEmail.trim();
    if (!email || resendInFlight.current) return;
    resendInFlight.current = true;
    setResendResult(null);
    setResending(true);
    try {
      const data = await api.post('/member-upload/resend-activation', { email });
      setResendResult(deriveResendResult({ ok: true }, data));
    } catch (err) {
      // `deriveResendResult` still owns every outcome's copy — it is handed the same shape it
      // always was, built from the thrown error's status and body. A request that never reached
      // the server has no status and keeps its own distinct message.
      setResendResult(err.status === undefined
        ? { type: 'error', message: 'We could not reach the server. Please check your connection and try again.' }
        : deriveResendResult({ ok: false, status: err.status }, err.data || {}));
    } finally {
      resendInFlight.current = false;
      setResending(false);
    }
  };

  const shell = (children) => (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.logo}>
          <img src="/images/logo.png" alt="Oval" />
        </div>
        {children}
      </div>
    </div>
  );

  if (checkState === CHECK.pending) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (checkState === CHECK.denied) {
    return shell(
      <div className={styles.denied}>
        <h2>Access Denied</h2>
        <p>Invalid or missing upload token. Please use the link provided by your administrator.</p>
      </div>
    );
  }

  if (checkState === CHECK.unavailable) {
    return shell(
      <div className={styles.denied}>
        <h2>We couldn&apos;t check your upload link</h2>
        <p>
          The server didn&apos;t respond, so we could not confirm your link. This is a problem on our
          side, not with your link. Please try again in a moment.
        </p>
        <div className={styles.retryRow}>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={runTokenCheck}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return shell(
    <>
      <div className={styles.header}>
          <h1>Upload Members</h1>
          <p className={styles.subtitle}>Add members individually or upload a CSV file</p>
        </div>

        {/* Single Member Form */}
        <div className={styles.card}>
          <h2>Add Single Member</h2>
          <p className={styles.cardSubtitle}>Fill in the member details below</p>
          <form onSubmit={handleSingleSubmit}>
            <div className={styles.formGrid}>
              <div className={`${styles.formGroup} ${styles.full}`}>
                <label>Email *</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="member@example.com" />
              </div>
              <div className={styles.formGroup}>
                <label>First Name *</label>
                <input type="text" name="first_name" value={form.first_name} onChange={handleChange} required placeholder="First name" />
              </div>
              <div className={styles.formGroup}>
                <label>Last Name *</label>
                <input type="text" name="last_name" value={form.last_name} onChange={handleChange} required placeholder="Last name" />
              </div>
              <div className={styles.formGroup}>
                <label>Phone</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="555-123-4567" />
              </div>
              <div className={styles.formGroup}>
                <label>Date of Birth</label>
                <input type="date" name="birth_date" value={form.birth_date} onChange={handleChange} />
              </div>
              <div className={`${styles.formGroup} ${styles.full}`}>
                <label>Address</label>
                <input type="text" name="address" value={form.address} onChange={handleChange} placeholder="Street address" />
              </div>
              <div className={styles.formGroup}>
                <label>City</label>
                <input type="text" name="city" value={form.city} onChange={handleChange} placeholder="City" />
              </div>
              <div className={styles.formGroup}>
                <label>State</label>
                <input type="text" name="state" value={form.state} onChange={handleChange} placeholder="State" maxLength={2} />
              </div>
              <div className={styles.formGroup}>
                <label>Zip</label>
                <input type="text" name="zip" value={form.zip} onChange={handleChange} placeholder="Zip code" maxLength={10} />
              </div>
              <div className={styles.formGroup}>
                <label>Gender</label>
                <select name="gender" value={form.gender} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className={styles.submitRow}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={submitting}>
                {submitting ? 'Creating...' : 'Add Member'}
              </button>
            </div>
          </form>
          {singleResult && (
            <div className={`${styles.alert} ${singleResult.type === 'success' ? styles.alertSuccess : styles.alertError}`}>
              {singleResult.message}
            </div>
          )}
        </div>

        {/* CSV Upload */}
        <div className={styles.card}>
          <h2>CSV Upload</h2>
          <p className={styles.cardSubtitle}>Upload multiple members at once</p>
          <div className={styles.instructions}>
            <strong>Required column:</strong> <code>email</code><br />
            <strong>Optional columns:</strong> <code>first_name</code>, <code>last_name</code>, <code>phone</code>, <code>address</code>, <code>city</code>, <code>state</code>, <code>zip</code>, <code>birth_date</code>, <code>gender</code>
          </div>
          <div className={styles.fileInputWrapper}>
            <label className={styles.fileLabel}>
              Choose CSV File
              <input type="file" accept=".csv" onChange={handleFileChange} ref={fileRef} style={{ display: 'none' }} />
            </label>
            {csvFile && <span className={styles.fileName}>{csvFile.name}</span>}
          </div>
          <div className={styles.submitRow}>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleCsvUpload} disabled={!csvFile || uploading}>
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </button>
          </div>
          {csvResult?.type === 'error' && (
            <div className={`${styles.alert} ${styles.alertError}`}>{csvResult.message}</div>
          )}
        </div>

        {/* Resend Activation Email */}
        <div className={styles.card}>
          <h2>Resend Activation Email</h2>
          <p className={styles.cardSubtitle}>Send a fresh set-password link to a member who has not activated yet.</p>
          <form onSubmit={handleResendSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="resend-activation-email">Member email</label>
              <input
                id="resend-activation-email"
                type="email"
                name="resend_email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="member@example.com"
                autoComplete="email"
              />
            </div>
            <div className={styles.submitRow}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={resending || !resendEmail.trim()}>
                {resending ? 'Sending...' : 'Resend activation email'}
              </button>
            </div>
          </form>
          {resendResult && (
            <div className={`${styles.alert} ${resendResult.type === 'success' ? styles.alertSuccess : styles.alertError}`}>
              {resendResult.message}
            </div>
          )}
        </div>

        {/* CSV Results */}
        {csvResult?.type === 'success' && (
          <div className={styles.resultsCard}>
            <h2>Upload Results</h2>
            <div className={styles.resultsSummary}>
              {csvResult.data.created > 0 && <span className={`${styles.resultBadge} ${styles.badgeCreated}`}>{csvResult.data.created} Created</span>}
              {csvResult.data.updated > 0 && <span className={`${styles.resultBadge} ${styles.badgeUpdated}`}>{csvResult.data.updated} Updated</span>}
              {csvResult.data.errors > 0 && <span className={`${styles.resultBadge} ${styles.badgeFailed}`}>{csvResult.data.errors} Failed</span>}
            </div>
            {csvResult.data.details?.length > 0 && (
              <table className={styles.resultsTable}>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {csvResult.data.details.map((row, i) => (
                    <tr key={i}>
                      <td>{row.row ?? i + 1}</td>
                      <td>{row.email || '—'}</td>
                      <td>
                        <span className={`${styles.statusDot} ${row.status === 'created' ? styles.dotCreated : row.status === 'updated' ? styles.dotUpdated : styles.dotFailed}`} />
                        {row.status}
                      </td>
                      <td>{row.error || row.message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
      )}
    </>
  );
};

export default UploadMembersPage;
